// Local persistence for user-created reports.
//
// Built-in templates are defined in `report-templates.js` and live in code.
// This module is concerned only with the user's own reports — saved to
// localStorage now, will be swapped for server-backed storage once
// permissions land. The exported API is intentionally narrow so that swap
// is a single-file change.

import { BUILT_IN_REPORTS } from './report-templates.js';

const STORAGE_KEY = 'kdb_reports_v1';
const SCHEMA = 1;

// Read the user-reports container. Returns {schema, reports[]} even when
// localStorage is unavailable (private mode, quota error) — callers always
// see a usable shape.
function readUserReports() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { schema: SCHEMA, reports: [] };
    const j = JSON.parse(raw);
    if (!j || typeof j !== 'object' || !Array.isArray(j.reports)) {
      return { schema: SCHEMA, reports: [] };
    }
    return j;
  } catch {
    return { schema: SCHEMA, reports: [] };
  }
}

function writeUserReports(state) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
  catch { /* private mode / quota — silently fall back to in-memory only */ }
}

// Random ID for user-created reports/panels. Built-ins use stable string
// IDs (defined in report-templates.js) so URLs stay bookmarkable.
function newId(prefix) {
  return prefix + '-' + Math.random().toString(36).slice(2, 10);
}

// List every report — built-ins first, then user reports newest-first.
// Each entry is the full Report shape; the caller can read .builtin to
// distinguish.
export function listReports() {
  const user = readUserReports().reports.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
  return [...BUILT_IN_REPORTS, ...user];
}

// Look up by id — checks built-ins first, then user reports.
export function getReport(id) {
  const built = BUILT_IN_REPORTS.find(r => r.id === id);
  if (built) return built;
  const user = readUserReports().reports.find(r => r.id === id);
  return user || null;
}

// Persist a user report. Refuses to overwrite a built-in id (caller must
// duplicate first). Returns the saved report.
export function saveReport(report) {
  if (BUILT_IN_REPORTS.some(r => r.id === report.id)) {
    throw new Error(`Cannot overwrite built-in report "${report.id}" — duplicate it first`);
  }
  const state = readUserReports();
  const idx = state.reports.findIndex(r => r.id === report.id);
  const next = { ...report, builtin: false, updatedAt: Date.now() };
  if (idx >= 0) state.reports[idx] = next;
  else state.reports.push(next);
  writeUserReports(state);
  return next;
}

// Remove a user report. Built-ins are immutable.
export function deleteReport(id) {
  if (BUILT_IN_REPORTS.some(r => r.id === id)) return false;
  const state = readUserReports();
  const before = state.reports.length;
  state.reports = state.reports.filter(r => r.id !== id);
  if (state.reports.length === before) return false;
  writeUserReports(state);
  return true;
}

// Clone a report (built-in or user) into the user's list. New random ids
// for the report and every panel; name gets a "(copy)" suffix. Returns the
// new Report.
export function duplicateReport(id) {
  const src = getReport(id);
  if (!src) throw new Error(`Report not found: ${id}`);
  const copy = {
    id: newId('rpt'),
    name: src.name + ' (copy)',
    description: src.description || '',
    builtin: false,
    panels: (src.panels || []).map(p => ({ ...p, id: newId('pnl') })),
    updatedAt: Date.now(),
  };
  const state = readUserReports();
  state.reports.push(copy);
  writeUserReports(state);
  return copy;
}

// Empty-shell report for the "+ New report" flow.
export function blankReport() {
  return {
    id: newId('rpt'),
    name: 'New report',
    description: '',
    builtin: false,
    panels: [],
    updatedAt: Date.now(),
  };
}

// Helper for tests / future server adapter — exported so callers can build
// fresh panels with consistent ids.
export function newPanelId() { return newId('pnl'); }
